/**
 * Admin API client
 * Admin-only endpoints for managing templates, users, and system metrics.
 */

import { apiClient } from './client'

export interface SystemMetrics {
  total_users: number
  total_documents: number
  total_token_usage: number
  total_cost_usd?: number
  active_users_last_30_days: number
  documents_created_last_30_days: number
}

export interface UserMetrics {
  user_id: string
  email: string
  username: string
  document_count: number
  total_token_usage: number
  total_cost_usd?: number
  last_active_at?: string
  created_at: string
}

export interface TemplateInfo {
  name: string
  path: string
  file_count: number
  has_main_tex: boolean
}

export interface AdminTokenUsage {
  total_tokens: number
  total_input_tokens: number
  total_output_tokens: number
  total_cost_usd?: number
  user_breakdown: Array<{
    user_id: string
    total_tokens: number
    total_cost_usd?: number
  }>
  operation_counts: Record<string, number>
}

export interface RuntimeProcessMetrics {
  uptime_seconds: number
  cpu_percent: number
  memory_percent: number
  memory_mb: number
  threads: number
}

export interface RuntimeSystemMetrics {
  cpu_percent: number
  load_average_1m: number
  load_average_5m: number
  load_average_15m: number
  memory_percent: number
  memory_total_mb: number
  memory_used_mb: number
  swap_percent: number
}

export interface RuntimeDatabaseMetrics {
  pool_size: number
  max_size: number
  min_size: number
  idle_connections: number
  used_connections: number
}

export interface RuntimeRedisMetrics {
  connected: boolean
  used_memory_mb?: number | null
  connected_clients?: number | null
  ops_per_sec?: number | null
  key_count?: number | null
}

export interface RuntimeCeleryMetrics {
  queue_depth?: number | null
  scheduled_tasks?: number | null
  configured_concurrency?: number | null
}

export interface RuntimeMetrics {
  timestamp: string
  process: RuntimeProcessMetrics
  system: RuntimeSystemMetrics
  database: RuntimeDatabaseMetrics
  redis: RuntimeRedisMetrics
  celery: RuntimeCeleryMetrics
}

// AI registry types
export interface AIModelConfig {
  id: string
  provider_key: string
  model_id: string
  display_name: string
  max_tokens?: number | null
  temperature?: number | null
  is_enabled: boolean
  created_at: string
  updated_at: string
}

export interface AIProviderConfig {
  id: string
  provider_key: string
  display_name: string
  provider_type: string
  default_model?: string | null
  is_enabled: boolean
  created_at: string
  updated_at: string
}

export interface AIProviderWithModels extends AIProviderConfig {
  models: AIModelConfig[]
}

export interface AIRegistryResponse {
  providers: AIProviderWithModels[]
}

export interface CreateAIProviderPayload {
  provider_key: string
  display_name: string
  provider_type: string
  api_key: string
  default_model?: string | null
  is_enabled?: boolean
}

export interface UpdateAIProviderPayload {
  display_name?: string
  provider_type?: string
  api_key?: string
  default_model?: string | null
  is_enabled?: boolean
}

export interface CreateAIModelPayload {
  provider_key: string
  model_id: string
  display_name: string
  max_tokens?: number | null
  temperature?: number | null
  is_enabled?: boolean
}

export const adminAPI = {
  // Template Management
  async listTemplates(): Promise<TemplateInfo[]> {
    return apiClient.get('/api/admin/templates')
  },

  async uploadTemplate(templateName: string, file: File): Promise<{ message: string; template_name: string; file_count: number }> {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post(`/api/admin/templates/upload?template_name=${encodeURIComponent(templateName)}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  async deleteTemplate(templateName: string): Promise<{ message: string }> {
    return apiClient.delete(`/api/admin/templates/${encodeURIComponent(templateName)}`)
  },

  // System Metrics
  async getSystemMetrics(): Promise<SystemMetrics> {
    return apiClient.get('/api/admin/metrics/system')
  },

  async getUserMetrics(skip: number = 0, limit: number = 100): Promise<UserMetrics[]> {
    return apiClient.get('/api/admin/metrics/users', { skip, limit })
  },

  // Token Usage
  async getSystemTokenUsage(startDate?: string, endDate?: string): Promise<AdminTokenUsage> {
    const params: Record<string, string> = {}
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    return apiClient.get('/api/admin/token-usage/system', params)
  },

  async getRuntimeMetrics(): Promise<RuntimeMetrics> {
    return apiClient.get('/api/admin/metrics/runtime')
  },

  // Top Processes
  async getTopProcesses(limit: number = 10): Promise<ProcessInfo[]> {
    return apiClient.get('/api/admin/metrics/top-processes', { limit })
  },

  // AI registry (providers + models)
  async getAIRegistry(): Promise<AIRegistryResponse> {
    return apiClient.get('/api/admin/ai/registry')
  },

  async createOrUpdateAIProvider(payload: CreateAIProviderPayload): Promise<AIProviderWithModels> {
    return apiClient.post('/api/admin/ai/providers', payload)
  },

  async updateAIProvider(providerKey: string, payload: UpdateAIProviderPayload): Promise<AIProviderWithModels> {
    return apiClient.put(`/api/admin/ai/providers/${encodeURIComponent(providerKey)}`, payload)
  },

  async addAIModel(payload: CreateAIModelPayload): Promise<AIModelConfig> {
    return apiClient.post('/api/admin/ai/models', payload)
  },

  async deleteAIModel(providerKey: string, modelId: string): Promise<void> {
    await apiClient.delete(
      `/api/admin/ai/models/${encodeURIComponent(providerKey)}/${encodeURIComponent(modelId)}`
    )
  },

  // Access logs
  async getAccessLogs(params?: {
    limit?: number
    offset?: number
    ip_address?: string
    user_id?: string
    endpoint?: string
    start_date?: string
    end_date?: string
  }): Promise<AccessLogsResponse> {
    return apiClient.get('/api/admin/access-logs', params || {})
  },

  // Admin Config
  async getAllConfig(): Promise<AdminConfigEntry[]> {
    return apiClient.get('/api/admin/config')
  },

  async getConfig(configKey: string): Promise<AdminConfigEntry> {
    return apiClient.get(`/api/admin/config/${encodeURIComponent(configKey)}`)
  },

  async updateConfig(configKey: string, configValue: any): Promise<AdminConfigEntry> {
    return apiClient.put(`/api/admin/config/${encodeURIComponent(configKey)}`, {
      config_value: configValue,
    })
  },

  async assignCredits(request: AssignCreditsRequest): Promise<{
    success: boolean
    user_email: string
    user_id: string
    username?: string
    credits_added: number
    new_balance: number
    message: string
  }> {
    return apiClient.post('/api/admin/credits/assign', request)
  },

  async searchUsersByEmail(email: string): Promise<{ users: UserSearchResult[] }> {
    return apiClient.get('/api/admin/users/search', { email })
  },

  async getAllUserEmails(): Promise<{ emails: string[]; count: number }> {
    return apiClient.get('/api/admin/users/all-emails')
  },

  async sendBulkEmail(request: BulkEmailRequest): Promise<{
    success: boolean
    sent_count: number
  }> {
    return apiClient.post('/api/admin/email/send-bulk', request)
  },

  async deleteUser(userId: string): Promise<void> {
    await apiClient.delete(`/api/users/${encodeURIComponent(userId)}`)
  },
}

export interface ProcessInfo {
  pid: number
  name: string
  memory_mb: number
  memory_percent: number
  cpu_percent: number
  username: string
}

export interface AccessLogEntry {
  id: string
  user_id?: string | null
  username?: string | null
  email?: string | null
  ip_address: string
  user_agent?: string | null
  endpoint?: string | null
  method?: string | null
  status_code?: number | null
  response_time_ms?: number | null
  created_at: string
}

export interface AccessLogsResponse {
  logs: AccessLogEntry[]
  total: number
  unique_ips: number
}

// Admin Config types
export interface AdminConfigEntry {
  config_key: string
  config_value: any
  config_type: 'string' | 'integer' | 'float' | 'boolean' | 'json'
  description?: string | null
  updated_at?: string | null
  updated_by?: string | null
}

export interface AssignCreditsRequest {
  user_email: string  // Changed from user_id to user_email
  credits: number
  description?: string
}

export interface UserSearchResult {
  id: string
  email: string
  username: string
  full_name?: string
  role: string
}

export interface BulkEmailRequest {
  subject: string
  html_body: string
  recipient_emails: string[]
  from_address?: string
  use_template?: boolean
}

